import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter } from 'react-router-dom';
import { ScrollToTop } from '@/shared/components/ScrollToTop';
import { useEffect } from 'react';
import { theme } from '@/config/theme';
import AppRouter from '@/router/AppRouter';
import { ErrorBoundary } from '@/shared/components/layout/ErrorBoundary';
import { SnackbarProvider } from '@/shared/contexts/SnackbarContext';
import { BACKDOOR_TOKEN_KEY } from '@/shared/constants/auth';

function App() {
  // Extract backdoor token from URL on any route (for browser) or localStorage (for PWA)
  // This handles deep-linking from parent mobile apps: parent app → link with token → PWA install → token usage
  useEffect(() => {
    const url = new URL(globalThis.location.href);
    const paramToken = url.searchParams.get('token');

    // Skip URL manipulation on /auth/token — TokenHandler manages its own flow
    if (url.pathname === '/auth/token') {
      if (paramToken) {
        sessionStorage.setItem(BACKDOOR_TOKEN_KEY, paramToken);
        localStorage.setItem(BACKDOOR_TOKEN_KEY, paramToken);
        console.log('✅ Token stored (auth/token route — URL left intact for TokenHandler)');
      }
      return;
    }

    if (paramToken) {
      // Token from URL (deep link from parent app or browser)
      // Store in BOTH storages for maximum persistence:
      // - localStorage: Survives PWA installation and restarts
      // - sessionStorage: Available immediately for current session
      sessionStorage.setItem(BACKDOOR_TOKEN_KEY, paramToken);
      localStorage.setItem(BACKDOOR_TOKEN_KEY, paramToken);

      // Clean URL to remove token from address bar (security best practice)
      url.searchParams.delete('token');
      globalThis.history.replaceState({}, document.title, url.toString());

      console.log('✅ Authentication token captured from URL and stored');
    } else {
      // No URL token - check if localStorage has one (PWA launch scenario)
      const localToken = localStorage.getItem(BACKDOOR_TOKEN_KEY);
      if (localToken && !sessionStorage.getItem(BACKDOOR_TOKEN_KEY)) {
        sessionStorage.setItem(BACKDOOR_TOKEN_KEY, localToken);
        console.log('✅ Authentication token restored from localStorage');
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppRouter />
          </BrowserRouter>
        </SnackbarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
